/**
 * Cashflow API hooks — manual TanStack Query wrappers around the cashflow
 * endpoints. These will be replaced by Orval-generated hooks once the backend
 * OpenAPI spec is regenerated.
 */

import { useMutation, useQuery, useQueryClient, type QueryClient, type QueryKey } from '@tanstack/react-query';
import { customInstance } from '../../services/api-client';
import type {
  ForecastSummary,
  ForecastVersion,
  WeeklyForecastRow,
  ARAgingRecord,
  APAgingRecord,
  AgingSummary,
  CashflowAction,
  ActionSummary,
  FacilityConfig,
  ForecastAssumption,
  CashPositionAccount,
  DataLoad,
  AssumptionOverride,
  ScenarioParams,
  ScenarioComputeResult,
  ConfidenceBand,
  PaginatedResponse,
  AppletDataSourceConfig,
  ConnectorOption,
  AvailabilityWaterfall,
  VarianceIntelligence,
  VarianceDetail,
  VarianceNarrative,
  MonthlyRollup,
  BudgetEngine,
  BudgetFinancial,
  CashConversionRule,
  RevenueDetail,
  FPAFeedback,
  WorkingCapitalRecommendation,
  DriftMonitor,
  ForecastDrift,
  DailyBankBalance,
  DailyBankTotal,
  MonitorSummary,
  MonitorAlertList,
  MonitorRun,
  MonitorDefinition,
  AccuracyData,
  DriftDataSummary,
  MonitorNotification,
  MonitorGroup,
} from './types';

const BASE = '/api/cashflow';
const CASHFLOW_SUMMARY_STALE_TIME_MS = 12 * 60 * 60 * 1000;
const CASHFLOW_SUMMARY_CACHE_TIME_MS = 7 * 24 * 60 * 60 * 1000;
const CASHFLOW_SUMMARY_STORAGE_PREFIX = 'eliza.cashflow.summary.v1';

type PersistedForecastSummary = {
  data: ForecastSummary | null;
  updatedAt: number;
  expiresAt: number;
};

type StagedCashflowRequest = {
  queryKey: QueryKey;
  queryFn: () => Promise<unknown>;
};

const keys = {
  all: ['cashflow'] as const,
  summary: (id?: string) => [...keys.all, 'summary', id] as const,
  versions: () => [...keys.all, 'versions'] as const,
  weekly: (id: string) => [...keys.all, 'weekly', id] as const,
  arAging: (params?: Record<string, any>) => [...keys.all, 'ar-aging', params] as const,
  arSummary: () => [...keys.all, 'ar-summary'] as const,
  apAging: (params?: Record<string, any>) => [...keys.all, 'ap-aging', params] as const,
  actions: (params?: Record<string, any>) => [...keys.all, 'actions', params] as const,
  actionSummary: () => [...keys.all, 'action-summary'] as const,
  facility: () => [...keys.all, 'facility'] as const,
  assumptions: (category?: string) => [...keys.all, 'assumptions', category] as const,
  cashPosition: () => [...keys.all, 'cash-position'] as const,
  dataLoads: () => [...keys.all, 'data-loads'] as const,
  overrides: (id: string) => [...keys.all, 'overrides', id] as const,
  confidence: (id: string) => [...keys.all, 'confidence', id] as const,
  scenarios: (id: string) => [...keys.all, 'scenarios', id] as const,
  availability: () => [...keys.all, 'availability'] as const,
  varianceIntelligence: (id: string) => [...keys.all, 'variance-intelligence', id] as const,
  monthlyRollup: (id: string) => [...keys.all, 'monthly-rollup', id] as const,
  budgetEngine: () => [...keys.all, 'budget-engine'] as const,
  fpaFeedback: (id: string) => [...keys.all, 'fpa-feedback', id] as const,
  workingCapital: (id: string) => [...keys.all, 'working-capital', id] as const,
  driftMonitor: (id: string) => [...keys.all, 'drift-monitor', id] as const,
  dataSourceConfig: () => [...keys.all, 'data-source-config'] as const,
  availableConnectors: () => [...keys.all, 'available-connectors'] as const,
  monitorSummary: () => [...keys.all, 'monitors', 'summary'] as const,
  monitorAlerts: (group?: string) => [...keys.all, 'monitors', 'alerts', group] as const,
  monitorRuns: () => [...keys.all, 'monitors', 'runs'] as const,
  monitorDefinitions: () => [...keys.all, 'monitors', 'definitions'] as const,
  monitorAccuracy: () => [...keys.all, 'monitors', 'accuracy'] as const,
  monitorDrift: () => [...keys.all, 'monitors', 'drift'] as const,
  monitorDataQuality: () => [...keys.all, 'monitors', 'data-quality'] as const,
  monitorNotifications: () => [...keys.all, 'monitors', 'notifications'] as const,
};

function getCashflowCacheScope(): string {
  if (typeof window === 'undefined') return 'server';

  try {
    const authStorage = window.localStorage.getItem('auth-storage');
    const authState = authStorage ? JSON.parse(authStorage)?.state : null;
    const user = authState?.user;
    const customerId = user?.customer_id || 'default';
    const userId = user?.id || user?.email || 'anonymous';
    return `${customerId}:${userId}`;
  } catch {
    return 'default:anonymous';
  }
}

function getForecastSummaryStorageKey(forecastId?: string): string {
  const scope = encodeURIComponent(getCashflowCacheScope());
  const forecastKey = encodeURIComponent(forecastId || 'latest');
  return `${CASHFLOW_SUMMARY_STORAGE_PREFIX}:${scope}:${forecastKey}`;
}

function readPersistedForecastSummary(forecastId?: string): PersistedForecastSummary | undefined {
  if (typeof window === 'undefined') return undefined;

  try {
    const raw = window.localStorage.getItem(getForecastSummaryStorageKey(forecastId));
    if (!raw) return undefined;

    const cached = JSON.parse(raw) as PersistedForecastSummary;
    if (!cached || cached.expiresAt <= Date.now()) {
      window.localStorage.removeItem(getForecastSummaryStorageKey(forecastId));
      return undefined;
    }

    return cached;
  } catch {
    return undefined;
  }
}

function writePersistedForecastSummary(forecastId: string | undefined, data: ForecastSummary | null) {
  if (typeof window === 'undefined') return;

  try {
    const now = Date.now();
    const payload: PersistedForecastSummary = {
      data,
      updatedAt: now,
      expiresAt: now + CASHFLOW_SUMMARY_CACHE_TIME_MS,
    };
    window.localStorage.setItem(getForecastSummaryStorageKey(forecastId), JSON.stringify(payload));
  } catch {
    // localStorage can be unavailable or full; React Query still keeps the in-memory result.
  }
}

function buildForecastSummaryUrl(forecastId?: string, forceRefresh = false): string {
  const base = forecastId
    ? `${BASE}/summary/${forecastId}`
    : `${BASE}/versions/latest`;
  return forceRefresh ? `${base}?force_refresh=true` : base;
}

async function fetchForecastSummary(
  forecastId?: string,
  forceRefresh = false,
  options: { persist?: boolean } = {},
): Promise<ForecastSummary | null> {
  const data = await customInstance<ForecastSummary | null>({
    url: buildForecastSummaryUrl(forecastId, forceRefresh),
    method: 'GET',
  });
  if (options.persist ?? true) {
    writePersistedForecastSummary(forecastId, data);
  }
  return data;
}

// ── Forecast ────────────────────────────────────────────────────────

export function useForecastSummary(forecastId?: string) {
  const persisted = readPersistedForecastSummary(forecastId);

  return useQuery<ForecastSummary | null>({
    queryKey: keys.summary(forecastId),
    queryFn: () => fetchForecastSummary(forecastId),
    initialData: persisted?.data,
    initialDataUpdatedAt: persisted?.updatedAt,
    staleTime: CASHFLOW_SUMMARY_STALE_TIME_MS,
    gcTime: CASHFLOW_SUMMARY_CACHE_TIME_MS,
    refetchOnMount: false,
    refetchOnReconnect: false,
    refetchOnWindowFocus: false,
  });
}

export function useForecastVersions() {
  return useQuery<ForecastVersion[]>({
    queryKey: keys.versions(),
    queryFn: () => customInstance({ url: `${BASE}/versions`, method: 'GET' }),
  });
}

export function useWeeklyForecast(forecastId: string) {
  return useQuery<WeeklyForecastRow[]>({
    queryKey: keys.weekly(forecastId),
    queryFn: () =>
      customInstance({ url: `${BASE}/summary/${forecastId}`, method: 'GET' }),
    enabled: !!forecastId,
  });
}

export function useGenerateForecast() {
  const qc = useQueryClient();
  return useMutation<{ task_id: string; status: string; message: string }>({
    mutationFn: () =>
      customInstance({ url: `${BASE}/forecast/generate`, method: 'POST' }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: keys.all });
    },
  });
}

export function useForecastTaskStatus(taskId: string | null) {
  const qc = useQueryClient();
  return useQuery<{ task_id: string; status: string; result?: any; error?: string }>({
    queryKey: ['cashflow', 'task-status', taskId],
    queryFn: () =>
      customInstance({ url: `${BASE}/forecast/status/${taskId}`, method: 'GET' }),
    enabled: !!taskId,
    refetchInterval: (query) => {
      const status = query.state.data?.status;
      if (status === 'SUCCESS' || status === 'FAILURE') return false;
      return 2000;
    },
    refetchOnWindowFocus: false,
  });
}

// ── AR Aging ────────────────────────────────────────────────────────

export function useARAgingPaginated(params: {
  page?: number;
  page_size?: number;
  entity?: string;
  bucket?: string;
  customer?: string;
  sort_by?: string;
  sort_dir?: string;
}) {
  return useQuery<PaginatedResponse<ARAgingRecord>>({
    queryKey: keys.arAging(params),
    queryFn: () => {
      const search = new URLSearchParams();
      Object.entries(params).forEach(([k, v]) => {
        if (v !== undefined && v !== '') search.set(k, String(v));
      });
      return customInstance({
        url: `${BASE}/ar-aging?${search.toString()}`,
        method: 'GET',
      });
    },
  });
}

export function useARSummary() {
  return useQuery<AgingSummary>({
    queryKey: keys.arSummary(),
    queryFn: () => customInstance({ url: `${BASE}/ar-aging/summary`, method: 'GET' }),
  });
}

// ── AP Aging ────────────────────────────────────────────────────────

export function useAPAgingPaginated(params: {
  page?: number;
  page_size?: number;
  entity?: string;
  criticality?: string;
  vendor?: string;
  sort_by?: string;
  sort_dir?: string;
}) {
  return useQuery<PaginatedResponse<APAgingRecord>>({
    queryKey: keys.apAging(params),
    queryFn: () => {
      const search = new URLSearchParams();
      Object.entries(params).forEach(([k, v]) => {
        if (v !== undefined && v !== '') search.set(k, String(v));
      });
      return customInstance({
        url: `${BASE}/ap-aging?${search.toString()}`,
        method: 'GET',
      });
    },
  });
}

// ── Actions ─────────────────────────────────────────────────────────

export function useActions(params?: { status?: string; forecast_id?: string; priority?: string }) {
  return useQuery<CashflowAction[]>({
    queryKey: keys.actions(params),
    queryFn: () => {
      const search = new URLSearchParams();
      if (params) {
        Object.entries(params).forEach(([k, v]) => {
          if (v) search.set(k, v);
        });
      }
      return customInstance({
        url: `${BASE}/actions?${search.toString()}`,
        method: 'GET',
      });
    },
  });
}

export function useActionSummary() {
  return useQuery<ActionSummary>({
    queryKey: keys.actionSummary(),
    queryFn: () =>
      customInstance({ url: `${BASE}/actions/summary`, method: 'GET' }),
  });
}

export function useUpdateAction() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, ...updates }: { id: number } & Partial<CashflowAction>) =>
      customInstance({
        url: `${BASE}/actions/${id}`,
        method: 'PUT',
        data: updates,
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: keys.actions() });
      qc.invalidateQueries({ queryKey: keys.actionSummary() });
    },
  });
}

// ── Facility ────────────────────────────────────────────────────────

export function useFacilityConfig() {
  return useQuery<FacilityConfig>({
    queryKey: keys.facility(),
    queryFn: () => customInstance({ url: `${BASE}/facility`, method: 'GET' }),
  });
}

// ── Assumptions ─────────────────────────────────────────────────────

export function useAssumptions(category?: string) {
  return useQuery<ForecastAssumption[]>({
    queryKey: keys.assumptions(category),
    queryFn: () => {
      const url = category
        ? `${BASE}/assumptions/${category}`
        : `${BASE}/assumptions`;
      return customInstance({ url, method: 'GET' });
    },
  });
}

export function useUpdateAssumption() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      category,
      key,
      value,
    }: {
      category: string;
      key: string;
      value: number | string;
    }) =>
      customInstance({
        url: `${BASE}/assumptions/${category}/${key}`,
        method: 'PUT',
        data: typeof value === 'number' ? { assumption_value: value } : { assumption_text: value },
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: keys.assumptions() });
    },
  });
}

// ── Cash Position ───────────────────────────────────────────────────

export function useCashPosition() {
  return useQuery<CashPositionAccount[]>({
    queryKey: keys.cashPosition(),
    queryFn: () => customInstance({ url: `${BASE}/cash-position`, method: 'GET' }),
  });
}

// ── Data Loads ──────────────────────────────────────────────────────

export function useDataLoads() {
  return useQuery<DataLoad[]>({
    queryKey: keys.dataLoads(),
    queryFn: () => customInstance({ url: `${BASE}/data-loads`, method: 'GET' }),
  });
}

// ── Overrides ───────────────────────────────────────────────────────

export function useOverrides(forecastId: string) {
  return useQuery<AssumptionOverride[]>({
    queryKey: keys.overrides(forecastId),
    queryFn: () =>
      customInstance({ url: `${BASE}/overrides/${forecastId}`, method: 'GET' }),
    enabled: !!forecastId,
  });
}

// ── Scenarios ───────────────────────────────────────────────────────

export function useComputeScenario() {
  return useMutation<ScenarioComputeResult, Error, { forecast_id: string } & ScenarioParams>({
    mutationFn: (params) =>
      customInstance({
        url: `${BASE}/scenarios/compute`,
        method: 'POST',
        data: params,
      }),
  });
}

// ── Confidence Bands ────────────────────────────────────────────────

export function useConfidenceBands(forecastId: string) {
  return useQuery<ConfidenceBand[]>({
    queryKey: keys.confidence(forecastId),
    queryFn: () =>
      customInstance({ url: `${BASE}/confidence/${forecastId}`, method: 'GET' }),
    enabled: !!forecastId,
  });
}

// ── Data Source Configuration ────────────────────────────────────────

export function useDataSourceConfig() {
  return useQuery<AppletDataSourceConfig>({
    queryKey: keys.dataSourceConfig(),
    queryFn: () =>
      customInstance({ url: `${BASE}/config/data-source`, method: 'GET' }),
  });
}

export function useAvailableConnectors() {
  return useQuery<ConnectorOption[]>({
    queryKey: keys.availableConnectors(),
    queryFn: () =>
      customInstance({ url: `${BASE}/config/available-connectors`, method: 'GET' }),
  });
}

export function useSetDataSource() {
  const qc = useQueryClient();
  return useMutation<AppletDataSourceConfig, Error, { connector_id: string; redshift_schema?: string }>({
    mutationFn: (params) =>
      customInstance({
        url: `${BASE}/config/data-source`,
        method: 'PUT',
        data: params,
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: keys.all });
    },
  });
}

export function useTestConnection() {
  return useMutation<{ status: string; redshift_connected: boolean; message: string }, Error, { connector_id: string; redshift_schema?: string }>({
    mutationFn: (params) =>
      customInstance({
        url: `${BASE}/config/test-connection`,
        method: 'POST',
        data: params,
      }),
  });
}

// ── Feature 1: Availability & Cash Truth ─────────────────────────────

export function useAvailability() {
  return useQuery<AvailabilityWaterfall>({
    queryKey: keys.availability(),
    queryFn: () => customInstance({ url: `${BASE}/availability`, method: 'GET' }),
  });
}

// ── Feature 2: Variance Intelligence ─────────────────────────────────

export function useVarianceIntelligence(forecastId: string) {
  return useQuery<VarianceIntelligence>({
    queryKey: keys.varianceIntelligence(forecastId),
    queryFn: () =>
      customInstance({ url: `${BASE}/variance-intelligence/${forecastId}`, method: 'GET' }),
    enabled: !!forecastId,
  });
}

// ── Feature 3: Monthly Rollup ────────────────────────────────────────

export function useMonthlyRollup(forecastId: string) {
  return useQuery<MonthlyRollup[]>({
    queryKey: keys.monthlyRollup(forecastId),
    queryFn: () =>
      customInstance({ url: `${BASE}/monthly-rollup/${forecastId}`, method: 'GET' }),
    enabled: !!forecastId,
  });
}

// ── Feature 4: Budget Engine ─────────────────────────────────────────

export function useBudgetEngine(fiscalYear: number = 2026) {
  return useQuery<BudgetEngine>({
    queryKey: keys.budgetEngine(),
    queryFn: () =>
      customInstance({ url: `${BASE}/budget-engine?fiscal_year=${fiscalYear}`, method: 'GET' }),
  });
}

// ── Feature 5: FP&A Feedback ─────────────────────────────────────────

export function useFPAFeedback(forecastId: string) {
  return useQuery<FPAFeedback[]>({
    queryKey: keys.fpaFeedback(forecastId),
    queryFn: () =>
      customInstance({ url: `${BASE}/fpa-feedback/${forecastId}`, method: 'GET' }),
    enabled: !!forecastId,
  });
}

export function useUpdateFPAFeedback() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, status }: { id: number; status: string }) =>
      customInstance({
        url: `${BASE}/fpa-feedback/${id}/status`,
        method: 'PUT',
        data: { status },
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: keys.all });
    },
  });
}

// ── Feature 6: Working Capital ───────────────────────────────────────

export function useWorkingCapital(forecastId: string, status?: string) {
  return useQuery<WorkingCapitalRecommendation[]>({
    queryKey: keys.workingCapital(forecastId),
    queryFn: () => {
      const params = status ? `?status=${status}` : '';
      return customInstance({
        url: `${BASE}/working-capital/${forecastId}${params}`,
        method: 'GET',
      });
    },
    enabled: !!forecastId,
  });
}

export function useUpdateWorkingCapital() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, status }: { id: number; status: string }) =>
      customInstance({
        url: `${BASE}/working-capital/${id}/status`,
        method: 'PUT',
        data: { status },
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: keys.all });
    },
  });
}

// ── Feature 7: Drift Monitor ─────────────────────────────────────────

export function useDriftMonitor(forecastId: string) {
  return useQuery<DriftMonitor>({
    queryKey: keys.driftMonitor(forecastId),
    queryFn: () =>
      customInstance({ url: `${BASE}/drift-monitor/${forecastId}`, method: 'GET' }),
    enabled: !!forecastId,
  });
}

// ── Feature 8: Cashflow Monitors ─────────────────────────────────────

const MONITORS_BASE = `${BASE}/monitors`;

export function useMonitorSummary() {
  return useQuery<MonitorSummary>({
    queryKey: keys.monitorSummary(),
    queryFn: () => customInstance({ url: `${MONITORS_BASE}/summary`, method: 'GET' }),
    refetchInterval: 60_000,
  });
}

export function useMonitorAlerts(group?: MonitorGroup | string) {
  const params = new URLSearchParams();
  if (group) params.set('monitor_group', group);
  const qs = params.toString();
  return useQuery<MonitorAlertList>({
    queryKey: keys.monitorAlerts(group),
    queryFn: () =>
      customInstance({ url: `${MONITORS_BASE}/alerts${qs ? `?${qs}` : ''}`, method: 'GET' }),
  });
}

export function useUpdateAlert() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ alertId, body }: { alertId: number; body: Record<string, any> }) =>
      customInstance({ url: `${MONITORS_BASE}/alerts/${alertId}`, method: 'PATCH', data: body }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: keys.monitorAlerts() });
      qc.invalidateQueries({ queryKey: keys.monitorSummary() });
    },
  });
}

export function useMonitorRuns() {
  return useQuery<MonitorRun[]>({
    queryKey: keys.monitorRuns(),
    queryFn: () => customInstance({ url: `${MONITORS_BASE}/runs`, method: 'GET' }),
  });
}

export function useTriggerMonitorRun() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: { agent_identifier: string }) =>
      customInstance({ url: `${MONITORS_BASE}/run`, method: 'POST', data: body }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: keys.monitorRuns() });
    },
  });
}

export function useMonitorDefinitions() {
  return useQuery<MonitorDefinition[]>({
    queryKey: keys.monitorDefinitions(),
    queryFn: () => customInstance({ url: `${MONITORS_BASE}/definitions`, method: 'GET' }),
  });
}

export function useAccuracyData(lineItem?: string) {
  const params = new URLSearchParams();
  if (lineItem) params.set('line_item', lineItem);
  const qs = params.toString();
  return useQuery<AccuracyData>({
    queryKey: keys.monitorAccuracy(),
    queryFn: () =>
      customInstance({ url: `${MONITORS_BASE}/accuracy/trend${qs ? `?${qs}` : ''}`, method: 'GET' }),
  });
}

export function useDataQualityStatus() {
  return useQuery({
    queryKey: keys.monitorDataQuality(),
    queryFn: () => customInstance({ url: `${MONITORS_BASE}/data-quality/status`, method: 'GET' }),
  });
}

export function useDriftSummary() {
  return useQuery<DriftDataSummary>({
    queryKey: keys.monitorDrift(),
    queryFn: () => customInstance({ url: `${MONITORS_BASE}/drift/summary`, method: 'GET' }),
  });
}

export function useMonitorNotifications(unreadOnly = false) {
  const params = new URLSearchParams();
  if (unreadOnly) params.set('unread_only', 'true');
  const qs = params.toString();
  return useQuery<MonitorNotification[]>({
    queryKey: keys.monitorNotifications(),
    queryFn: () =>
      customInstance({ url: `${MONITORS_BASE}/notifications${qs ? `?${qs}` : ''}`, method: 'GET' }),
    refetchInterval: 30_000,
  });
}

export function useMarkNotificationRead() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (notifId: number) =>
      customInstance({ url: `${MONITORS_BASE}/notifications/${notifId}/read`, method: 'PATCH' }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: keys.monitorNotifications() });
    },
  });
}

export function useMarkAllNotificationsRead() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () =>
      customInstance({ url: `${MONITORS_BASE}/notifications/mark-all-read`, method: 'POST' }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: keys.monitorNotifications() });
    },
  });
}

function buildCashflowRefreshRequests(forecastId?: string): StagedCashflowRequest[] {
  const arDefaults = {
    page: 1,
    page_size: 25,
    entity: undefined,
    bucket: undefined,
    customer: undefined,
    sort_by: 'open_balance',
    sort_dir: 'DESC',
  };
  const apDefaults = {
    page: 1,
    page_size: 25,
    entity: undefined,
    criticality: undefined,
    vendor: undefined,
    sort_by: 'open_balance',
    sort_dir: 'DESC',
  };

  const requests: StagedCashflowRequest[] = [
    { queryKey: keys.versions(), queryFn: () => customInstance({ url: `${BASE}/versions`, method: 'GET' }) },
    { queryKey: keys.arSummary(), queryFn: () => customInstance({ url: `${BASE}/ar-aging/summary`, method: 'GET' }) },
    { queryKey: keys.arAging(arDefaults), queryFn: () => customInstance({ url: `${BASE}/ar-aging?page=1&page_size=25&sort_by=open_balance&sort_dir=DESC`, method: 'GET' }) },
    { queryKey: keys.apAging(apDefaults), queryFn: () => customInstance({ url: `${BASE}/ap-aging?page=1&page_size=25&sort_by=open_balance&sort_dir=DESC`, method: 'GET' }) },
    { queryKey: keys.actions({ status: 'open' }), queryFn: () => customInstance({ url: `${BASE}/actions?status=open`, method: 'GET' }) },
    { queryKey: keys.actionSummary(), queryFn: () => customInstance({ url: `${BASE}/actions/summary`, method: 'GET' }) },
    { queryKey: keys.facility(), queryFn: () => customInstance({ url: `${BASE}/facility`, method: 'GET' }) },
    { queryKey: keys.cashPosition(), queryFn: () => customInstance({ url: `${BASE}/cash-position`, method: 'GET' }) },
    { queryKey: keys.dataLoads(), queryFn: () => customInstance({ url: `${BASE}/data-loads`, method: 'GET' }) },
    { queryKey: keys.assumptions(), queryFn: () => customInstance({ url: `${BASE}/assumptions`, method: 'GET' }) },
    { queryKey: keys.availability(), queryFn: () => customInstance({ url: `${BASE}/availability`, method: 'GET' }) },
    { queryKey: keys.budgetEngine(), queryFn: () => customInstance({ url: `${BASE}/budget-engine?fiscal_year=2026`, method: 'GET' }) },
    { queryKey: keys.dataSourceConfig(), queryFn: () => customInstance({ url: `${BASE}/config/data-source`, method: 'GET' }) },
    { queryKey: keys.monitorSummary(), queryFn: () => customInstance({ url: `${MONITORS_BASE}/summary`, method: 'GET' }) },
    { queryKey: keys.monitorAlerts(), queryFn: () => customInstance({ url: `${MONITORS_BASE}/alerts`, method: 'GET' }) },
    { queryKey: keys.monitorRuns(), queryFn: () => customInstance({ url: `${MONITORS_BASE}/runs`, method: 'GET' }) },
    { queryKey: keys.monitorDefinitions(), queryFn: () => customInstance({ url: `${MONITORS_BASE}/definitions`, method: 'GET' }) },
    { queryKey: keys.monitorAccuracy(), queryFn: () => customInstance({ url: `${MONITORS_BASE}/accuracy/trend`, method: 'GET' }) },
    { queryKey: keys.monitorDataQuality(), queryFn: () => customInstance({ url: `${MONITORS_BASE}/data-quality/status`, method: 'GET' }) },
    { queryKey: keys.monitorDrift(), queryFn: () => customInstance({ url: `${MONITORS_BASE}/drift/summary`, method: 'GET' }) },
    { queryKey: keys.monitorNotifications(), queryFn: () => customInstance({ url: `${MONITORS_BASE}/notifications`, method: 'GET' }) },
  ];

  if (forecastId) {
    requests.push(
      { queryKey: keys.monthlyRollup(forecastId), queryFn: () => customInstance({ url: `${BASE}/monthly-rollup/${forecastId}`, method: 'GET' }) },
      { queryKey: keys.varianceIntelligence(forecastId), queryFn: () => customInstance({ url: `${BASE}/variance-intelligence/${forecastId}`, method: 'GET' }) },
      { queryKey: keys.fpaFeedback(forecastId), queryFn: () => customInstance({ url: `${BASE}/fpa-feedback/${forecastId}`, method: 'GET' }) },
      { queryKey: keys.workingCapital(forecastId), queryFn: () => customInstance({ url: `${BASE}/working-capital/${forecastId}`, method: 'GET' }) },
      { queryKey: keys.driftMonitor(forecastId), queryFn: () => customInstance({ url: `${BASE}/drift-monitor/${forecastId}`, method: 'GET' }) },
    );
  }

  return requests;
}

export async function prefetchAllCashflowData(
  qc: QueryClient,
  options: { forceRefresh?: boolean } = {},
) {
  const forceRefresh = options.forceRefresh ?? false;
  const summary = await fetchForecastSummary(undefined, forceRefresh, { persist: false });
  const requests: StagedCashflowRequest[] = [
    { queryKey: keys.summary(), queryFn: () => Promise.resolve(summary) },
    ...buildCashflowRefreshRequests(summary?.forecast_id),
  ];
  const results = await Promise.allSettled(requests.map((request) => request.queryFn()));

  results.forEach((result, index) => {
    if (result.status === 'fulfilled') {
      qc.setQueryData(requests[index].queryKey, result.value);
      if (index === 0) {
        writePersistedForecastSummary(undefined, result.value as ForecastSummary | null);
      }
    }
  });
}

export async function refreshAllCashflowData(qc: QueryClient) {
  await qc.invalidateQueries({ queryKey: keys.all, refetchType: 'none' });
  await prefetchAllCashflowData(qc, { forceRefresh: true });
}

export function useRefreshAllCashflowData() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => refreshAllCashflowData(qc),
  });
}

export { keys as cashflowQueryKeys };
